/**
 * Firestore refs that are accessed.
 */
import {
  CollectionReference,
  Firestore,
  collection,
  doc,
} from "firebase/firestore/lite";

const firestoreCollections = {
  restaurants: "restaurants-v1",
  menuUrls: "menu-urls",
  branding: "branding-v1",
  qrCodes: "qrCodes-v1",
  termsAndConditions: "t-and-cs",
  paymentPlans: "restaurant-payment-plans",
  suppliers: "suppliers-v1",

  restaurantLegal: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/restaurantLegal/${restaurantUuid}`,

  locations: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/locations`,

  // Menus & Dishes
  restaurantMenus: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/restaurantMenus/${restaurantUuid}`,
  menus: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/menus`,
  menuSections: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/menuSections`,
  dishes: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/dishes`,
  products: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/products`,
  integrations: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/integrations`,
};

export class FirestoreRefs {
  firestore: Firestore;

  constructor(firestore: Firestore) {
    this.firestore = firestore;
  }

  public restaurants = () =>
    collection(this.firestore, firestoreCollections.restaurants);
  public restaurant = (uuid: string) =>
    doc(this.firestore, firestoreCollections.restaurants, uuid);

  public locations = (restaurantUuid: string) =>
    collection(this.firestore, firestoreCollections.locations(restaurantUuid));

  public location = (restaurantUuid: string, locationId: string) =>
    doc(
      this.firestore,
      firestoreCollections.locations(restaurantUuid),
      locationId
    );

  public igfdMenuUrls = () =>
    collection(this.firestore, firestoreCollections.menuUrls);

  public qrCodes = () =>
    collection(this.firestore, firestoreCollections.qrCodes);

  public branding = (restaurantUuid: string) =>
    doc(this.firestore, firestoreCollections.branding, restaurantUuid);

  public termsAndConditions = (tcId: string) =>
    doc(this.firestore, firestoreCollections.termsAndConditions, tcId);

  public restaurantTermsAcceptance = (restaurantUuid: string) =>
    doc(this.firestore, firestoreCollections.restaurantLegal(restaurantUuid));

  // Payment Plan
  public paymentPlans = () =>
    collection(this.firestore, firestoreCollections.paymentPlans);

  public paymentPlan = (restaurantUuid: string) =>
    doc(this.firestore, firestoreCollections.paymentPlans, restaurantUuid);

  // Menus & Dishes
  // Menu related metadata at a restaurant level
  public restaurantMenu = (restaurantUuid: string) =>
    doc(this.firestore, firestoreCollections.restaurantMenus(restaurantUuid));
  public menus = (restaurantUuid: string) =>
    collection(this.firestore, firestoreCollections.menus(restaurantUuid));

  public menu = (restaurantUuid: string, menuId: string) =>
    doc(this.firestore, firestoreCollections.menus(restaurantUuid), menuId);

  public menuSections = (restaurantUuid: string) =>
    collection(
      this.firestore,
      firestoreCollections.menuSections(restaurantUuid)
    );

  public menuSection = (restaurantUuid: string, menuSectionId: string) =>
    doc(
      this.firestore,
      firestoreCollections.menuSections(restaurantUuid),
      menuSectionId
    );

  public dishes = (restaurantUuid: string) =>
    collection(this.firestore, firestoreCollections.dishes(restaurantUuid));

  public dish = (restaurantUuid: string, dishId: string) =>
    doc(this.firestore, firestoreCollections.dishes(restaurantUuid), dishId);

  public products = (restaurantUuid: string) =>
    collection(this.firestore, firestoreCollections.products(restaurantUuid));

  public product = (restaurantUuid: string, productId: string) =>
    doc(
      this.firestore,
      firestoreCollections.products(restaurantUuid),
      productId
    );

  // Integrations
  public integrations = (restaurantUuid: string) =>
    collection(
      this.firestore,
      firestoreCollections.integrations(restaurantUuid)
    );

  public integration = (restaurantUuid: string, integrationId: string) =>
    doc(
      this.firestore,
      firestoreCollections.integrations(restaurantUuid),
      integrationId
    );

  // for a given restaurant manager, this returns the list of restaurants that are accessible to them
  public managerAccessibleRestaurants = (uid: string): CollectionReference =>
    collection(
      this.firestore,
      `restaurant-manager/${uid}/accessible-restaurants`
    );

  // Existence of this doc indicates that the user is an admin
  public adminUser = (uid: string) => doc(this.firestore, `admin-users/${uid}`);

  // Suppliers
  public suppliers = () =>
    collection(this.firestore, firestoreCollections.suppliers);

  public supplier = (supplierId: string) =>
    doc(this.firestore, firestoreCollections.suppliers, supplierId);
}

// The following allows for tree-shaking of the unused refs, and these aren't used in all apps.
export const dashboardAdminViewHistoryColRef = (
  firestore: Firestore,
  uid: string
) => collection(firestore, `admin-users/${uid}/dashboard-view-history`);
export const dashboardAdminViewHistoryRef = (
  firestore: Firestore,
  uid: string,
  restaurantUuid: string
) =>
  doc(firestore, `admin-users/${uid}/dashboard-view-history/${restaurantUuid}`);

// Other root collections
export const analyticsWatermarkColRef = (firestore: Firestore) =>
  collection(firestore, "restaurant-analytics-watermark");

// Other sub collections
export const restaurantSubColRef = (
  firestore: Firestore,
  restaurantUuid: string,
  collectionName: string
) =>
  collection(
    firestore,
    `${firestoreCollections.restaurants}/${restaurantUuid}/${collectionName}`
  );
export const locationSubColRef = (
  firestore: Firestore,
  restaurantUuid: string,
  locationId: string,
  collectionName: string
) =>
  collection(
    firestore,
    `${firestoreCollections.locations(
      restaurantUuid
    )}/${locationId}/${collectionName}`
  );
